import type {
  AssistantMessagesPage,
  AssistantThread,
  AssistantThreadsPage,
} from '@agently/types';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
} from 'react-query-kit';

import { assistantApi } from './client';
import { dedupeById } from './dedupe';
import { useGetThreadMessages } from './messages';

const THREADS_PAGE_SIZE = 20;

const useThreadsPages = createInfiniteQuery({
  queryKey: ['assistant', 'threads', 'list'],
  fetcher: async (
    _variables: void,
    { pageParam }: { pageParam: unknown },
  ): Promise<AssistantThreadsPage> => {
    const { data } = await assistantApi.get<AssistantThreadsPage>('/threads', {
      params: { limit: THREADS_PAGE_SIZE, offset: pageParam },
    });
    return data;
  },
  initialPageParam: 0,
  // Next offset = rows loaded so far; pages may overlap when the list shifts.
  getNextPageParam: (lastPage, allPages) =>
    lastPage.hasNext
      ? allPages.reduce((loaded, page) => loaded + page.threads.length, 0)
      : undefined,
  staleTime: 30_000,
});

const flattenThreads = (data: InfiniteData<AssistantThreadsPage>) =>
  dedupeById(data.pages.flatMap((page) => page.threads));

export const useGetAssistantThreads = Object.assign(
  () => useThreadsPages({ select: flattenThreads }),
  {
    getKey: useThreadsPages.getKey,
    getFetchOptions: useThreadsPages.getFetchOptions,
  },
);

export const useGetAssistantThread = createQuery({
  queryKey: ['assistant', 'threads', 'detail'],
  fetcher: async ({ threadId }: { threadId: string }) => {
    const { data } = await assistantApi.get<AssistantThread>(
      `/threads/${threadId}`,
    );
    return data;
  },
});

export const useCreateAssistantThread = createMutation({
  mutationKey: ['assistant', 'threads', 'create'],
  mutationFn: async () => {
    const { data } = await assistantApi.post<AssistantThread>('/threads', {
      title: null,
    });
    return data;
  },
  onSuccess: (thread, _variables, _result, { client }) => {
    client.setQueryData(
      useGetAssistantThread.getKey({ threadId: thread.id }),
      thread,
    );
    client.setQueryData<
      InfiniteData<AssistantMessagesPage, string | undefined>
    >(useGetThreadMessages.getKey({ threadId: thread.id }), {
      pages: [{ messages: [], hasMore: false }],
      pageParams: [undefined],
    });
    void client.invalidateQueries({
      queryKey: useGetAssistantThreads.getKey(),
    });
  },
});

// Every cached page of the Threads list, mapped in place (the list is an infinite query).
const updateListPages = (
  client: QueryClient,
  update: (threads: AssistantThread[]) => AssistantThread[],
) => {
  client.setQueryData<InfiniteData<AssistantThreadsPage, number>>(
    useGetAssistantThreads.getKey(),
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          threads: update(page.threads),
        })),
      },
  );
};

/** A Thread that no longer exists: drop its queries and its row, and refresh the list. */
export const forgetThread = (client: QueryClient, threadId: string) => {
  client.removeQueries({
    queryKey: useGetAssistantThread.getKey({ threadId }),
  });
  client.removeQueries({
    queryKey: useGetThreadMessages.getKey({ threadId }),
  });
  updateListPages(client, (threads) =>
    threads.filter((thread) => thread.id !== threadId),
  );
  void client.invalidateQueries({ queryKey: useGetAssistantThreads.getKey() });
};

export const useRenameAssistantThread = createMutation({
  mutationKey: ['assistant', 'threads', 'rename'],
  mutationFn: async ({
    threadId,
    title,
  }: {
    threadId: string;
    title: string;
  }) => {
    const { data } = await assistantApi.patch<AssistantThread>(
      `/threads/${threadId}`,
      { title },
    );
    return data;
  },
  onError: (error, _variables, _result, { client }) => {
    // Renaming a Thread that is gone: the list is stale.
    if (isAxiosError(error) && error.response?.status === 404) {
      void client.invalidateQueries({
        queryKey: useGetAssistantThreads.getKey(),
      });
    }
  },
  onSuccess: (renamed, _variables, _result, { client }) => {
    client.setQueryData(
      useGetAssistantThread.getKey({ threadId: renamed.id }),
      renamed,
    );
    updateListPages(client, (threads) =>
      threads.map((thread) => (thread.id === renamed.id ? renamed : thread)),
    );
    void client.invalidateQueries({
      queryKey: useGetAssistantThreads.getKey(),
    });
  },
});

export const useDeleteAssistantThread = createMutation({
  mutationKey: ['assistant', 'threads', 'delete'],
  mutationFn: async ({ threadId }: { threadId: string }) => {
    try {
      await assistantApi.delete(`/threads/${threadId}`);
    } catch (error) {
      // Already gone elsewhere: the outcome is what was asked for.
      if (!(isAxiosError(error) && error.response?.status === 404)) {
        throw error;
      }
    }
  },
  onSuccess: (_data, { threadId }, _result, { client }) => {
    forgetThread(client, threadId);
  },
});
